import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from "react-router";
import {ErrorCode} from "../../types/Error";
import Topbar from "../Topbar";
import {useTranslation} from "react-i18next";

export enum ErrorParam {
	KEY = "key",
	REQUESTED_PAGE = "requested-page",
	ACCESS_CODE_ERROR_CODE = "code",
	REQUESTED_SERVICE = "requested-service"
}

export default function ErrorPage() {
	const {t} = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [key, setKey] = useState<ErrorCode>(ErrorCode.UNKNOWN_REASON);

	useEffect(() => {
		const keyParam = getParamAndDelete(ErrorParam.KEY) ?? ErrorCode.UNKNOWN_REASON;
		setKey(keyParam as ErrorCode);
	}, []);

	const getParamAndDelete = useCallback((paramName: ErrorParam): string | null => {
		const paramValue = searchParams.get(paramName);
		searchParams.delete(paramName);
		setSearchParams(searchParams);

		return paramValue;
	}, [searchParams]);

	const details = useMemo(() => {
		switch (key) {
			case ErrorCode.PAGE_NOT_FOUND: {
				const page = getParamAndDelete(ErrorParam.REQUESTED_PAGE);
				return t("error_pageNotFound", {page});
			}
			case ErrorCode.ACCESS_CODE_ERROR: {
				const code = getParamAndDelete(ErrorParam.ACCESS_CODE_ERROR_CODE);

				let message = t("error_accessCodeValidationError")
				if (code == ErrorCode.EMPTY_ACCESS_CODE) {
					message += ` ${t("error_emptyAccessCode")}`
				} else if (code == ErrorCode.ACCESS_CODE_ALREADY_CONSUMED) {
					message += ` ${t("error_consumedAccessCode")}`
				} else if (code == ErrorCode.ACCESS_CODE_EXPIRED) {
					message += ` ${t("error_expiredAccessCode")}`
				} else if (code == ErrorCode.ACCESS_CODE_NOT_FOUND) {
					message += ` ${t("error_accessCodeNotFound")}`
				} else if (code == ErrorCode.ACCESS_CODE_INVALID) {
					message += ` ${t("error_invalidAccessCode")}`
				} else if (code == ErrorCode.GENERIC_ERROR) {
					message += ` ${t("error_accessCodeGenericError")}`
				}

				message += ` ${t("error_contactSupport")}`
				return message;
			}
			case ErrorCode.AUTHENTICATION_ERROR:
				return `${t("error_authenticationError")} ${t("error_contactSupport")}`
			case ErrorCode.UNAUTHORIZED:
				return `${t("error_unauthorized")} ${t("error_contactSupport")}`
			case ErrorCode.ILLEGAL_CONFIGURATIONS_STATE:
				return `${t("error_illegalConfigurationState")} ${t("error_contactSupport")}`
			case ErrorCode.SERVICE_NOT_FOUND: {
				const service = getParamAndDelete(ErrorParam.REQUESTED_SERVICE);
				return t("error_serviceNotFound", {service});
			}
			default:
				return null;
		}
	}, [key]);

	return (
			<React.Fragment>
				<Topbar/>
				<div id="error-page-container">
					<div id="error-page-title">{t("error_popupTitle")}</div>
					<div id="error-page-body">
						{t("error_popupBody")}
						{key != ErrorCode.UNKNOWN_REASON && (
								<div id="error-page-reasons-grid">
									<div>{t("error_reason")}:</div>
									<div className="error-key-cell">{key}</div>
									{details && (
											<React.Fragment>
												<div>{t("error_details")}:</div>
												<div>{details ?? `${t("error_defaultDetails")} ${t("error_contactSupport")}`}</div>
											</React.Fragment>
									)}
								</div>
						)}
					</div>
				</div>
			</React.Fragment>
	);
}
